import MenuIcon from '@material-symbols/svg-300/rounded/menu.svg'
import React, { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import CreateAssetForm from '../../components/assetForms/CreateAssetForm/CreateAssetForm'

import Button from '../../components/basics/Button/Button'
import ConfirmDialog from '../../components/basics/ConfirmDialog/ConfirmDialog'
import Menu from '../../components/basics/Menu/Menu'
import EditAssetForm from '../../components/assetForms/EditAssetForm/EditAssetForm'
import { Asset } from '../../models/Asset'
import isAssetVisible from '../../state/calculations/isAssetVisible'
import { useCanManageLearningItemAssets } from '../../state/calculations/permissions/userCan'
import { useLearningItem } from '../../state/entities'
import useDeleteAsset from '../../state/mutations/useDeleteAsset'
import useAssets from '../../state/queries/useAssets'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import useDialog from '../../utilities/useDialog'

import styles from './CoursePage.module.scss'

/**********************************************************************
 * AssetVisibleBetween
 **********************************************************************/
const AssetVisibleBetween = React.memo(function AssetVisibleBetween({
  asset,
  canManageAssets,
}: {
  asset: Asset
  canManageAssets: boolean
}) {
  const { t } = useTranslation()

  const visibilityTimes = (() => {
    if (asset.visibleFrom && canManageAssets) {
      if (asset.visibleUntil) {
        return t('asset.asset visible between.from <visibleFrom> until <visibleUntil>', {
          visibleFrom: asset.visibleFrom,
          visibleUntil: asset.visibleUntil,
        })
      }
      return t('asset.asset visible between.from <visibleFrom>', { visibleFrom: asset.visibleFrom })
    }
    if (asset.visibleUntil) {
      return t('asset.asset visible between.until <visibleUntil>', { visibleUntil: asset.visibleUntil })
    }
  })()

  const visibility = !isAssetVisible(asset) && t('asset.asset not visible')

  const output = [visibilityTimes, visibility].filter(Boolean).join(', ')

  return output ? <span className={styles.AssetVisibleBetween}>{output}</span> : null
})

/**********************************************************************
 * CoursePageAsset
 **********************************************************************/
const CoursePageAsset = React.memo(function CoursePageAsset({
  asset,
  canManageAssets,
}: {
  asset: Asset
  canManageAssets: boolean
}) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const showDialog = useDialog()

  const actionMenuItems = []

  // Delete asset
  const deleteAsset = useDeleteAsset()
  const handleDeleteAsset = useCallback(async () => {
    const confirmed = await showDialog(
      (close) => (
        <ConfirmDialog close={close} yesLabel={t('generic cta.yes')} noLabel={t('generic cta.cancel')}>
          <div>
            <Trans t={t} i18nKey="asset.should asset {filename} be deleted">
              {{ filename: asset.filename }}
            </Trans>
          </div>
        </ConfirmDialog>
      ),
      () => false
    )
    if (confirmed) {
      deleteAsset.mutate({ learningItemUuid: asset.learningItemUuid, assetUuid: asset.uuid })
    }
  }, [deleteAsset, asset])
  if (canManageAssets) {
    actionMenuItems.push(
      <li key="delete-asset">
        <Button onClick={handleDeleteAsset} menuItem>
          {t('asset.cta delete asset')}
        </Button>
      </li>
    )
  }

  // Edit asset
  const handleEditAsset = useCallback(async () => {
    await showDialog<void>((close) => (
      <EditAssetForm learningItemUuid={asset.learningItemUuid} assetUuid={asset.uuid} onClose={close} />
    ))
  }, [])
  if (canManageAssets) {
    actionMenuItems.push(
      <li key="edit-asset">
        <Button onClick={handleEditAsset} menuItem>
          {t('asset.cta edit asset')}
        </Button>
      </li>
    )
  }

  return (
    <section className={styles.CoursePageAsset}>
      <header>
        <h2>
          <Link
            to={routes.courseAsset.url({ uuid: asset.learningItemUuid, assetUuid: asset.uuid })}
            preventScrollReset={true}
          >
            {asset.filename}{' '}
            {typeof asset.byteSize === 'number' ? `(${t('format.BYTES', { bytes: asset.byteSize })})` : null}
          </Link>
        </h2>
        {actionMenuItems.length > 0 && (
          <Menu small label={<MenuIcon />} className={styles.actionMenu}>
            {actionMenuItems}
          </Menu>
        )}
      </header>
      <main>
        <AssetVisibleBetween asset={asset} canManageAssets={canManageAssets} />
        {Boolean(asset.description) && <div>{asset.description}</div>}
      </main>
    </section>
  )
})

/**********************************************************************
 * CoursePageAssets
 **********************************************************************/
export default React.memo(function CoursePageAssets({ learningItemUuid }: { learningItemUuid: string }) {
  const { t } = useTranslation()
  const showDialog = useDialog()
  const { user: currentUser } = useSession()

  const { learningItem } = useLearningItem(learningItemUuid)
  const { assets } = useAssets(learningItemUuid)

  const canManageAssets = useCanManageLearningItemAssets(currentUser, learningItem)

  const handleCreateAsset = useCallback(
    () => showDialog<void>((close) => <CreateAssetForm learningItemUuid={learningItemUuid} onClose={close} />),
    [learningItemUuid]
  )

  if (!canManageAssets && !assets.length) {
    return null
  }

  return (
    <section className={styles.CoursePageAssets}>
      <header>
        <h1>{t('learning item.field assets')}</h1>
        {canManageAssets && (
          <Button filled small onClick={handleCreateAsset}>
            {t('asset.cta new asset')}
          </Button>
        )}
      </header>

      <main>
        <ul>
          {assets.map((asset) => (
            <li key={asset.uuid}>
              <CoursePageAsset asset={asset} canManageAssets={canManageAssets} />
            </li>
          ))}
          {!assets.length && <li className={styles.empty}>{t('learning item.assets empty')}</li>}
        </ul>
      </main>
    </section>
  )
})
